<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <TransitionRoot as="template" :show="multiCameraDevicesStore.isOpenModal">
    <Dialog
      as="div"
      class="fixed z-20 inset-0 overflow-y-auto"
      @close="multiCameraDevicesStore.closeAVSettingsModal"
    >
      <div
        class="
          flex
          items-end
          justify-center
          min-h-screen
          pt-4
          px-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              relative
              inline-block
              align-bottom
              bg-vice-light-grey
              rounded-lg
              p-6
              text-left
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-lg
              sm:w-full
              sm:p-6
              container
            "
          >
            <div class="sm:flex flex-col sm:items-start">
              <!-- Media Device Selection -->
              <h2 class="w-full font-bold text-center mb-1">Audio and video settings</h2>
              <div class="flex justify-between flex-wrap">
                <template
                  v-for="(
                    mediaTypeProps, mediaType
                  ) in multiCameraDevicesStore.groupedDeviceAsAudioAndVideo"
                  :key="mediaType"
                >
                  <hr class="w-4/5 mx-auto h-px bg-black my-4" />
                  <h2 class="w-full font-bold mb-1">{{ mediaType }}</h2>
                  <div
                    class="w-full"
                    v-for="(deviceProps, deviceName) in mediaTypeProps"
                    :key="deviceName"
                  >
                    <div class="relative block w-full text-left text-sm mb-2">
                      <div class="block">
                        <span class="block mt-2 mb-1">
                          <span v-if="deviceName === DEVICE_NAME.CAMERA">
                            <span v-if="selectedCameraType === CAMERA_TYPES.INTERNAL_WEBCAM">
                              Main {{ deviceName.toLowerCase() }}
                            </span>
                            <span v-else-if="selectedCameraType === CAMERA_TYPES.EXTERNAL_WEBCAM">
                              Secondary {{ deviceName.toLowerCase() }}
                            </span>
                          </span>
                          <span v-else>{{ deviceName }}</span>
                        </span>
                        <button
                          type="button"
                          :disabled="isDisabledAudioSelection(mediaType)"
                          :class="[
                            `inline-flex
                            justify-center
                            w-full
                            rounded-md
                            border border-gray-300
                            shadow-sm
                            px-4
                            py-2
                            bg-white
                            text-sm
                            font-medium
                            text-gray-700
                            hover:bg-gray-50`,
                            multiCameraDevicesStore.externalAudioIO
                              ? 'pointer-events-none opacity-70'
                              : '',
                            isDisabledAudioSelection(mediaType)
                              ? 'disabled:cursor-not-allowed'
                              : '',
                          ]"
                          :id="`${deviceName}-button`"
                          aria-expanded="true"
                          aria-haspopup="true"
                          @click="handleDropdownsState(deviceName)"
                        >
                          {{ audioDeviceDisplay(deviceProps, deviceName) }}
                          <svg
                            class="-mr-1 ml-2 h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <!-- eslint-disable-next-line max-len -->
                            <path
                              fill-rule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>

                      <div
                        class="
                          origin-top-right
                          absolute
                          right-0
                          mt-2
                          w-full
                          z-10
                          rounded-md
                          shadow-lg
                          bg-white
                          ring-1 ring-black ring-opacity-5
                          divide-y divide-gray-100
                          focus:outline-none
                        "
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="menu-button"
                        tabindex="-1"
                        v-if="isActiveDevice(deviceName)"
                        v-click-outside="
                          () => {
                            handleDropdownsState();
                          }
                        "
                      >
                        <template v-for="(prop, index) in deviceProps" :key="prop.deviceId">
                          <div class="py-1" role="none">
                            <span
                              href="#"
                              class="text-gray-700 block pl-2 py-1 text-sm cursor-pointer"
                              role="menuitem"
                              tabindex="-1"
                              :id="`${deviceName}-${index}`"
                              @click="chooseMediaSource(deviceName, prop)"
                            >
                              {{ prop.label }}
                              <span
                                v-if="
                                  multiCameraDevicesStore.getConnectedCamerasLabels.includes(
                                    prop.label,
                                  ) ||
                                  multiCameraDevicesStore.selectedMicDeviceId === prop.deviceId ||
                                  multiCameraDevicesStore.selectedSpeakerDeviceId === prop.deviceId
                                "
                                style="font-weight: bold; color: crimson"
                              >
                                (in use)
                              </span>
                            </span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import vClickOutside from 'click-outside-vue3';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { computed } from 'vue';
import { SMART_GLASS_DEVICE_LABEL, CAMERA_TYPES, DEVICE_NAME } from '@/Utils/vice-constants';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';

export default {
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  setup() {
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const selectedCameraType = computed(() => multiCameraDevicesStore.currentActiveCameraType);

    const audioDeviceDisplay = (deviceProps, deviceName) =>
      deviceProps[0]?.kind.startsWith('audio') && multiCameraDevicesStore.externalAudioIO
        ? SMART_GLASS_DEVICE_LABEL
        : multiCameraDevicesStore.activeDeviceLabels[deviceName];

    const handleDropdownsState = (deviceName) => {
      if (deviceName) {
        multiCameraDevicesStore.handleDropdownsState(selectedCameraType.value, deviceName);
      } else {
        multiCameraDevicesStore.closeDropdowns();
      }
    };

    const isActiveDevice = (deviceName) =>
      multiCameraDevicesStore.allCameraTypes[selectedCameraType.value].deviceSelection[deviceName]
        .active;

    const chooseMediaSource = (deviceName, prop) => {
      multiCameraDevicesStore.chooseMediaSource(selectedCameraType.value, deviceName, prop);
    };

    // Allow audio selection for just the internal camera so that we have only one source
    const isDisabledAudioSelection = (mediaType) =>
      selectedCameraType.value !== CAMERA_TYPES.INTERNAL_WEBCAM && mediaType === 'AUDIO';

    return {
      CAMERA_TYPES,
      DEVICE_NAME,
      isActiveDevice,
      chooseMediaSource,
      selectedCameraType,
      audioDeviceDisplay,
      handleDropdownsState,
      multiCameraDevicesStore,
      isDisabledAudioSelection,
    };
  },
};
</script>

<style lang="scss" scoped>
.container {
  min-height: 300px;
}
</style>
